import { useState } from 'react';
import { ExternalLink, Filter, RefreshCw } from 'lucide-react';

import { useLeads } from '../hooks';
import { LEAD_STATUSES, PROJECT_TYPES } from '../types';
import type { LeadDTO, LeadFilters } from '../types';
import { LeadDrawer } from './LeadDrawer';

function scoreClass(score: number): string {
  if (score >= 80) return 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30';
  if (score >= 60) return 'bg-amber-500/20 text-amber-300 ring-1 ring-amber-500/30';
  return 'bg-white/5 text-white/60 ring-1 ring-white/10';
}

function statusClass(status: string): string {
  switch (status) {
    case 'new':
    case 'scored':
      return 'bg-sky-500/20 text-sky-300';
    case 'outreach_queued':
    case 'outreach_sent':
      return 'bg-violet-500/20 text-violet-300';
    case 'responded':
      return 'bg-emerald-500/20 text-emerald-300';
    case 'handed_off':
      return 'bg-cyan-500/20 text-cyan-300';
    case 'dismissed':
      return 'bg-white/5 text-white/40';
    default:
      return 'bg-white/5 text-white/70';
  }
}

export function LeadsTable() {
  const [filters, setFilters] = useState<LeadFilters>({ min_score: 40 });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const { leads, loading, error, refetch, offset, setOffset, hasMore } = useLeads(filters, 50);

  return (
    <div className="space-y-4">
      <div className="o-card flex flex-wrap items-end gap-3">
        <div className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-white/60">
          <Filter size={14} />
          Filtros
        </div>
        <label className="flex flex-col text-xs">
          <span className="mb-1 text-white/50">Estatus</span>
          <select
            className="input-base"
            value={filters.status || ''}
            onChange={(e) => {
              setOffset(0);
              setFilters((f) => ({ ...f, status: e.target.value || undefined }));
            }}
          >
            <option value="">Todos</option>
            {LEAD_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-xs">
          <span className="mb-1 text-white/50">Tipo proyecto</span>
          <select
            className="input-base"
            value={filters.project_type || ''}
            onChange={(e) => {
              setOffset(0);
              setFilters((f) => ({ ...f, project_type: e.target.value || undefined }));
            }}
          >
            <option value="">Todos</option>
            {PROJECT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col text-xs">
          <span className="mb-1 text-white/50">Estado</span>
          <input
            type="text"
            placeholder="DURANGO, JALISCO..."
            className="input-base"
            value={filters.state || ''}
            onChange={(e) => {
              setOffset(0);
              setFilters((f) => ({ ...f, state: e.target.value || undefined }));
            }}
          />
        </label>
        <label className="flex flex-col text-xs">
          <span className="mb-1 text-white/50">Score mínimo</span>
          <input
            type="number"
            min={0}
            max={100}
            className="input-base w-24"
            value={filters.min_score ?? 40}
            onChange={(e) => {
              setOffset(0);
              setFilters((f) => ({ ...f, min_score: Number(e.target.value) }));
            }}
          />
        </label>
        <button
          type="button"
          onClick={() => refetch()}
          className="o-btn-ghost ml-auto flex items-center gap-1 text-xs"
        >
          <RefreshCw size={12} />
          Refrescar
        </button>
      </div>

      {error ? (
        <div className="o-card border-red-500/30 text-sm text-red-300">{error}</div>
      ) : null}

      <div className="o-card overflow-x-auto !p-0">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-white/50">
            <tr className="border-b border-white/10">
              <th className="px-3 py-3 text-left">Score</th>
              <th className="px-3 py-3 text-left">Título</th>
              <th className="px-3 py-3 text-left">Tipo</th>
              <th className="px-3 py-3 text-left">Estado</th>
              <th className="px-3 py-3 text-left">Inv. match</th>
              <th className="px-3 py-3 text-left">Estatus</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && leads.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-sm text-white/40">Cargando leads...</td></tr>
            ) : leads.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-sm text-white/40">Sin leads que cumplan los filtros.</td></tr>
            ) : (
              leads.map((lead: LeadDTO) => (
                <tr
                  key={lead.id}
                  className="cursor-pointer border-t border-white/5 text-white/80 transition hover:bg-white/[0.04]"
                  onClick={() => setSelectedId(lead.id)}
                >
                  <td className="px-3 py-2">
                    <span className={`inline-flex h-7 w-12 items-center justify-center rounded-full text-xs font-semibold ${scoreClass(lead.score)}`}>
                      {lead.score}
                    </span>
                  </td>
                  <td className="max-w-md truncate px-3 py-2" title={lead.project.title}>
                    {lead.project.title}
                  </td>
                  <td className="px-3 py-2 text-xs text-white/60">{lead.project.project_type}</td>
                  <td className="px-3 py-2 text-xs text-white/60">{lead.project.state || '—'}</td>
                  <td className="px-3 py-2 text-xs">
                    {lead.matched_inventory_count > 0 ? (
                      <span className="font-medium text-emerald-300">{lead.matched_inventory_count} items</span>
                    ) : (
                      <span className="text-white/30">0</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${statusClass(lead.status)}`}>
                      {lead.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {lead.project.fuente_url ? (
                      <a
                        href={lead.project.fuente_url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title="Abrir en Compranet"
                        className="inline-flex text-white/40 hover:text-white"
                      >
                        <ExternalLink size={14} />
                      </a>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-xs text-white/50">
        <div>Mostrando {leads.length} resultados desde offset {offset}</div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - 50))}
            className="o-btn-ghost disabled:opacity-30"
          >
            Anterior
          </button>
          <button
            type="button"
            disabled={!hasMore}
            onClick={() => setOffset(offset + 50)}
            className="o-btn-ghost disabled:opacity-30"
          >
            Siguiente
          </button>
        </div>
      </div>

      <LeadDrawer
        leadId={selectedId}
        onClose={() => setSelectedId(null)}
        onDismissed={() => {
          setSelectedId(null);
          refetch();
        }}
      />
    </div>
  );
}
